import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import HandoffRequestDialog from './HandoffRequestDialog';

/**
 * The rare-find prompt (B6, the user's decision): the moment the AI names a
 * rare species at High certainty (utils/handoffs.js rareFindOf), the
 * collector -- still standing by the find -- is asked to collect it for the
 * lab: a pickup or a drop-off, and in Observation mode first "Collect it as
 * a specimen" (it then gets a code when it is sent).
 *
 * Also shows a request already made (`request`), with Change and Don't ask.
 * The host keeps the request: on the batch until it is sent
 * (fieldBatches.withHandoffRequest; the outbox sends it once the record is),
 * or straight to POST /handoffs for a record already on the server.
 *
 *   find        {aiId, taxonName, reasons} or null
 *   request     the request made, or null
 *   location    {lat, lng} for "Meet here"
 *   mayCollect  show "Collect it as a specimen" (Observation mode, a person
 *               who may register specimens, the batch's code not yet fixed)
 *   onCollect   () => Promise
 *   onRequest   (request, aiId) => Promise
 *   onRemove    () => Promise
 *   sentNote    what to say of a request made: 'withRecord' (sent with the
 *               record) or 'sent' (already with the lab)
 */
export default function RareFindPrompt({
  find, request = null, location = null, mayCollect = false, disabled = false,
  onCollect, onRequest, onRemove, sentNote = 'withRecord', testId = 'rare-find',
}) {
  const { t } = useTranslation(['handoffs', 'common']);
  const [asking, setAsking] = useState(null);
  const [failure, setFailure] = useState('');
  if (!find && !request) return null;
  const why = (find?.reasons || []).map(r => t(`reasons.${r}`)).join(', ');

  const run = async (fn) => {
    setFailure('');
    try {
      await fn();
    } catch (e) {
      setFailure(e?.message || t('form.failed'));
    }
  };

  return (
    <div role="note" aria-labelledby={`${testId}-title`} style={st.box} data-testid={testId}>
      <h4 id={`${testId}-title`} style={st.title}>{find ? t('prompt.title') : t('prompt.requestedTitle')}</h4>
      {find && (
        <p style={st.body} data-testid={`${testId}-why`}>
          {t('prompt.body', { taxon: find.taxonName, why })}
        </p>
      )}
      {request ? (
        <>
          <p style={st.body} data-testid={`${testId}-requested`} data-method={request.method}>
            {t(`prompt.${sentNote === 'sent' ? 'requestedSent' : 'requested'}`, { method: t(`method.${request.method}`) })}
          </p>
          {sentNote !== 'sent' && (
            <div style={st.actions}>
              <Button onClick={() => setAsking(request.method)} disabled={disabled} title={t('tip.change')}
                data-testid={`${testId}-change`}>
                {t('prompt.change')}
              </Button>
              <Button variant="ghost" onClick={() => run(() => onRemove?.())} disabled={disabled}
                title={t('tip.remove')} data-testid={`${testId}-remove`}>
                {t('prompt.remove')}
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <p style={st.hint}>{t('prompt.ask')}</p>
          <div style={st.actions}>
            {mayCollect && (
              <Button onClick={() => run(() => onCollect?.())} disabled={disabled} title={t('tip.collect')}
                data-testid={`${testId}-collect`}>
                {t('prompt.collect')}
              </Button>
            )}
            <Button variant="primary" onClick={() => setAsking('pickup')} disabled={disabled} title={t('tip.pickup')}
              data-testid={`${testId}-pickup`}>
              {t('prompt.pickup')}
            </Button>
            <Button onClick={() => setAsking('drop_off')} disabled={disabled} title={t('tip.dropOff')}
              data-testid={`${testId}-drop-off`}>
              {t('prompt.dropOff')}
            </Button>
          </div>
          {mayCollect && <p style={st.hint}>{t('prompt.collectHint')}</p>}
        </>
      )}
      {failure && <p role="alert" style={st.failure}>{failure}</p>}
      {asking && (
        <HandoffRequestDialog initial={{ ...(request || {}), method: asking }} location={location}
          testId={`${testId}-form`}
          onClose={() => setAsking(null)}
          onSubmit={async (made) => {
            await onRequest?.(made, find?.aiId || request?.aiId || null);
            setAsking(null);
          }} />
      )}
    </div>
  );
}

const st = {
  box: { border: '1px solid var(--c-warning-fg)', background: 'var(--c-warning-soft)', borderRadius: 8,
    padding: '10px 12px', margin: '10px 0' },
  title: { margin: '0 0 6px', fontSize: 15 },
  body: { margin: '0 0 8px', fontSize: 14 },
  hint: { margin: '0 0 8px', fontSize: 13, color: 'var(--c-text-muted-fg)' },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 },
  failure: { fontSize: 13, color: 'var(--c-danger-fg)', margin: '6px 0 0' },
};
